/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Xaero Zero - xaerozero@icloud.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ComponentFactoryResolver,
  HostListener, forwardRef} from '@angular/core';
import {fromEvent, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, map} from 'rxjs/operators';
import {ScrollToService} from '@nicky-lenaers/ngx-scroll-to';
import {User, Privilege, Role, ReportingPreset, RPi} from 'src/app/model';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import { SearchableItem, SearchableProvider, SearchableSelection } from 'src/app/model/interface';
import { CreateNewTypes } from 'src/app/model/enums/create.type';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Ibis } from 'src/app/model/ibis';
import { RoT } from 'src/app/model/rot';
import { Sic } from 'src/app/model/sic';
import { PresetValue } from 'src/app/model/preset-value';
import { LogService } from 'src/app/services';


const noop = (): any => {};

@Component({
  selector: 'dropdown-input-search',
  templateUrl: 'input-search.component.html',
  styleUrls: ['input-search.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputSearchComponent),
      multi: true
    }
  ],
})
export class InputSearchComponent implements OnInit, ControlValueAccessor {
  // @ViewChild('rootElement') rootElement: ElementRef;
  // @ViewChild('inputText') inputRef: ElementRef;

  @ViewChild('rootElement', {static: true}) rootElement: ElementRef;
  @ViewChild('inputText', {static: true}) inputRef: ElementRef;
  @ViewChild('container') containerRef: ElementRef;

  @Input() items: SearchableItem[] = [];
  @Input() preset: SearchableItem[];
  @Input() regular = false;
  @Input() required = false;
  @Input() provider: SearchableProvider;
  @Input() placeholder = 'Type anything you want';
  @Input('no-drop-icon') isNoDropIcon = false;
  @Input() showCreate = false;
  @Input() createNew: CreateNewTypes;
  @Input('parent-id') parentId: any;
  @Input() disabled = false;
  @Input() name: string;
  @Input() presetHeader: string = null;
  @Input() itemsHeader: string = null;
  @Input() userInputAllowed = false;
  @Input() showManage = false;
  @Input() refresh = false;
  // Value to store in ngModel
  @Input() inputValue: any;
  @Input() displayFull = false;
  @Input() paginated = false;
  @Output() valueChange: EventEmitter<SearchableSelection> = new EventEmitter();
  @Output() searchTriggered: EventEmitter<string> = new EventEmitter();

  searchQuery = '';
  page = 1;
  totalItems = 0;
  isLoadMore = false;

  private onChange: (_: any) => void = noop;
  private onTouched: () => void = noop;

  isOpen = false;
  selectedIndex: any = null;
  createModal: BsModalRef;
  topFlag = false;
  isLoading = false;

  constructor(
    private _scrollToService: ScrollToService,
    private resolver: ComponentFactoryResolver,
    private modalService: BsModalService,
    public logService : LogService
  ) {}

  get value(): any {
    return this.inputValue;
  }

  set value(val: any) {
    this.inputValue = val;
    this.onChange(val);
  }

  registerOnChange(fn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn): void {
    this.onTouched = fn;
  }

  writeValue(value: any): void {
    this.inputValue = value;
  }

  ngOnInit(): void {
    if (this.regular && this.provider) {
      this.searchForQuery('');
    } else if ( !this.regular && (this.provider || !this.provider)) {
      const eventStream = fromEvent(this.inputRef.nativeElement, 'keyup')
        .pipe(
          map(() => this.searchQuery),
          debounceTime(300),
          distinctUntilChanged());

      eventStream.subscribe(input => {
        this.searchTriggered.emit(input);
        if (input) {
          if (!this.isNoDropIcon) {
            this.page = 1;
            this.isLoadMore = false;
            this.isOpen = true;
            this.searchForQuery(input);
          }
        } else { this.isOpen = false; }
      });
    }
  }

  getValue(value: any): any {
    if (this.preset) {
      for (const v of this.preset) {
        if (v.getValue() === value) {
          if (this.displayFull) {
            return v.getFullDetails();
          }
          return v.getName();
        }
      }
    }

    if (this.items) {
      for (const v of this.items) {
        if (v.getValue() === value) {
          if (this.displayFull) {
            return v.getFullDetails();
          }
          return v.getName();
        }
      }
    }

    if (typeof value.getName === 'function') {
      return value.getName();
    }

    return value;
  }

  onSelected(selected: SearchableItem): void {
    // Emit Event that item is Selected
    this.valueChange.emit(new SearchableSelection(selected));

    // Close Modal
    this.isOpen = false;

    // Clear Search Query
    if (!this.userInputAllowed) {
      this.searchQuery = '';
    }

    // Write Value
    this.writeValue(selected.getValue());

    // Propagate ngModel Change
    this.onChange(selected.getValue());
  }

  onShow(): void {
    if (!this.disabled) {
      this.isOpen = !this.isOpen;

      if (!this.regular && !this.isNoDropIcon) {
        if (this.items.length === 0 || this.refresh) {
          this.page = 1;
          this.isLoadMore = false;
          this.searchForQuery('');
        }
      }

      if (this.isOpen) {
        if (this.isInViewport()) {
          this.topFlag = false;
        } else {
          this.topFlag = true;
        }
      }
    }
  }

  onClear(): void {
    // Clear Search Query
    this.searchQuery = '';

    // Write Value
    this.writeValue(null);

    this.selectedIndex = null;

    // Emit Event that item is reset
    this.valueChange.emit(null);
  }

  /**
   * FIXME: Needs Fixing to be Abstract
   * */
  searchForQuery(query: string): void {
    // Related to Parent, if its User or Client,
    // this param will be ignored in implementation of interface
    if (this.provider) {
      if (!this.isLoadMore)
        this.isLoading = true;

      this.provider.search(query, this.parentId, false,false, this.page)
        .subscribe(data => {
          if (data.success) {
            switch (this.provider.getSearchType()) {
            case 'USER':
              this.items = User.parseArray(data.result.items);
              break;
            case 'CLIENT':
              // this.items = Client.parseArray(data.result.items);
              break;
            case 'CLIENT_USER':
              // this.items = ClientUser.parseArray(data.result.items);
              break;
            case 'PRIVILEGE':
              this.items = Privilege.parseArray(data.result.items);
              break;
            case 'ROLE':
              this.items = Role.parseArray(data.result.items);
              break;
            case 'STAGE':
              // this.items = Stage.parseArray(data.result);
              break;
            case 'STATE':
              // this.items = State.parseArray(data.result);
              break;
            case 'PRESET':
              this.items = ReportingPreset.parseArray(data.result.items);
              break;
           
            case 'IBIS':
              this.items = Ibis.parseArray(data.result.items);
              break;
            case 'RoT':
              this.items = RoT.parseArray(data.result.items);
              break;
            case 'RPI':
              this.items = RPi.parseArray(data.result.items);
              break;
            case 'SIC':
              if(this.isLoadMore)
                this.appendItems(Sic.parseArray(data.result.items));
              else
                this.items = Sic.parseArray(data.result.items);
              break;
            default:
              break;
            }
            this.totalItems = data.result.totalItems;
            this.isLoading = false;
          }
        }, error => {
          // Don't forget to clear loading when on error
          this.isLoading = false;
          this.logService.error(error);
        });
    }
  }

  @HostListener('scroll', ['$event'])
  onScrollHandler(): void {
    if (this.containerRef.nativeElement.scrollHeight - this.containerRef.nativeElement.scrollTop === this.containerRef.nativeElement.clientHeight) {
      if (this.totalItems > this.items.length && this.paginated) {
        this.page += 1;
        this.isLoadMore = true;
        this.searchForQuery(this.searchQuery);
      }
    }
  }

  @HostListener('document:click', ['$event.target'])
  clickedOutside(targetElement: any): void {
    const clickedInside = this.rootElement.nativeElement.contains(targetElement);
    if (!clickedInside) {
      this.isOpen = false;
    }
  }

  onKeyArrowUp(event: any): void {
    event.preventDefault();

    if (this.items) {
      if (this.items.length > 0) {
        if (this.selectedIndex === 0) {
          this.selectedIndex = null;
          this.items[0].active = false;
          return;
        }

        if (this.selectedIndex !== null) {
          // Increment Index
          this.selectedIndex--;

          // 'Over', clear rest
          for (let i = 0; i < this.items.length; i++) {
            this.items[i].active = this.selectedIndex === i;
          }

          if (this.items[this.selectedIndex]) {
            this._scrollToService.scrollTo({
              target: 'ITEMS_' + this.selectedIndex,
              container: 'DROPDOWN_ITEMS',
              duration: 650,
              easing: 'easeOutElastic',
              offset: - 25 * 5
            });
          }
        }
      }
    }
  }

  onKeyArrowDown(event: any): void {
    event.preventDefault();

    if (this.items) {
      if (this.items.length > 0) {
        if (this.selectedIndex === null) {
          this.selectedIndex = 0;
          this.items[0].active = true;
          return;
        } else {
          if ( this.selectedIndex < this.items.length - 1 ) {
            this.selectedIndex++;

            for (let i = 0; i < this.items.length; i++) {
              this.items[i].active = this.selectedIndex === i;
            }

            if (this.items[this.selectedIndex]) {
              this._scrollToService.scrollTo({
                target: 'ITEMS_' + this.selectedIndex,
                container: 'DROPDOWN_ITEMS',
                duration: 650,
                easing: 'easeOutElastic',
                offset: - 25 * 5
              });
            }
          }
        }
      }
    }
  }

  onKeyEnter(event: any): void {
    event.preventDefault();

    if (this.selectedIndex !== null && this.items) {
      if (this.items[this.selectedIndex]) {
        this.onSelected(this.items[this.selectedIndex]);
      }
    } else if (this.userInputAllowed) {
      this.onSelected(PresetValue.create(this.searchQuery, this.searchQuery));
    }
  }

  onBlur(): void {
    if (this.userInputAllowed) {
      this.onSelected(PresetValue.create(this.searchQuery, this.searchQuery));
    }
  }

  createNewAction(): void {
    switch (this.createNew) {
    // FIXME: This need REDO to more abstract types
    /*case CreateNewTypes.CLIENT:
      this.createModal = this.modalService.show(CreateNewClientModal);
      break;
    case CreateNewTypes.CLIENT_USER:
      if (this.parentId) {
        this.createModal = this.modalService.show(CreateNewClientUserModal);
        this.createModal.content.clientId = this.parentId;
      } else { this.createModal = null; }
      break;*/
    default:
      this.createModal = null;
      this.logService.error(`Unknown Create Type used [${this.createNew}]`);
      break;
    }

    if (this.createModal) {
      const ackSubject = new Subject<SearchableItem>();
      this.createModal.content.subject = ackSubject;
      ackSubject.subscribe(value => {
        if (value) {
          if (!this.items) {
            this.items = [];
          }

          this.items.push(value);
          this.onSelected(value);
        }
      });
    } else { this.logService.error('Unknown Create Type used or Parent ID not Specified'); }
  }

  isInViewport(): boolean {
    if (this.rootElement.nativeElement) {
      const bounding = this.rootElement.nativeElement.getBoundingClientRect();
      return (
        bounding.top >= 0 &&
        bounding.bottom + 200 <= (window.innerHeight || document.documentElement.clientHeight));
    }

    return false;
  }

  onManage(): void {
    // FIXME: Needs REDOING
    // this.reportingService.onManage.emit();
  }

  appendItems(dataItem: SearchableItem[]): void {
    for (const im of dataItem) {
      this.items.push(im);
    }
  }
}
